// Foco's legal page and every earlier version of it. Each version is a file in shared/legal/, named by the
// day it took effect, with its text written out in full (no shared constants), so what an account accepted
// can be shown and sent word for word: on /legal?v=<version>, in the purchase emails (server/billing.ts) and
// with `npm run studio -- terms EMAIL`. Accounts record the version they accepted at sign-up, and every
// checkout the version its buyer accepted (Store.accept).
//
// To change the legal text: copy the newest file to one dated the day it goes live, edit that copy, close the
// previous entry below (`until`) and add the new one with its fingerprint (tests/legal.test.ts prints it).
// Never edit a published version.
import { type Lang, LOCALE } from "./i18n";
import v2026_10_13 from "./legal/2026-10-13";
import v2026_10_21 from "./legal/2026-10-21";

export type LegalPart = { h?: string; p?: string; list?: string[] };
export type LegalSection = { title: string; parts: LegalPart[] };
export const LEGAL_SECTIONS = [
  "notice",
  "privacy",
  "terms",
  "cookies",
] as const;
export type LegalText = {
  title: string;
  sections: Record<(typeof LEGAL_SECTIONS)[number], LegalSection>;
};
export type LegalVersion = {
  /** The day it took effect (Madrid), which names it. */
  version: string;
  /** The day the next version replaced it; null while it is current. */
  until: string | null;
  text: { en: LegalText; es: LegalText };
  /** SHA-256 of JSON.stringify(text): a published version never changes. */
  sha256: string;
};

/** The legal page's public address: Stripe's terms checkbox and the purchase emails link to it. */
export const LEGAL_URL = "https://foco.superintelect.io/legal";

/** Oldest first. */
export const LEGAL_VERSIONS: readonly LegalVersion[] = [
  {
    version: "2026-10-13",
    until: "2026-10-21",
    text: v2026_10_13,
    sha256: "7573af02a3ec34e4a78dd06ce89161cd3733e9e349c8d29af9d30b78626591ca",
  },
  {
    version: "2026-10-21",
    until: null,
    text: v2026_10_21,
    sha256: "a4e947af26d8b31e5dea374c6b51680bb6d7d1af64e6ba1eaaab9b562d74f16f",
  },
];

const CURRENT = LEGAL_VERSIONS[LEGAL_VERSIONS.length - 1];
/** The version in force, which a new account or a purchase accepts. */
export const TERMS_VERSION = CURRENT.version;

export const legalVersion = (version: string | null | undefined) =>
  LEGAL_VERSIONS.find((v) => v.version === version);

/** The version in force at a moment (an ISO time), by Madrid's calendar. */
export function versionAt(at: string | number | Date) {
  const day = new Date(at).toLocaleDateString("sv-SE", {
    timeZone: "Europe/Madrid",
  });
  return (
    [...LEGAL_VERSIONS].reverse().find((v) => v.version <= day) ??
    LEGAL_VERSIONS[0]
  ).version;
}

/** A version's day as people read it: "21 de octubre de 2026", "21 October 2026". */
export const legalDate = (day: string, lang: Lang) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString(LOCALE[lang], {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

const DOCUMENT = {
  en: {
    file: (v: string) => `foco-terms-${v}.txt`,
    updated: (day: string) => `Last updated: ${day}`,
    valid: (from: string, until: string | null) =>
      until
        ? `Version in force from ${from} to ${until}.`
        : `Version in force since ${from}.`,
  },
  es: {
    file: (v: string) => `foco-condiciones-${v}.txt`,
    updated: (day: string) => `Última actualización: ${day}`,
    valid: (from: string, until: string | null) =>
      until
        ? `Versión vigente del ${from} al ${until}.`
        : `Versión vigente desde el ${from}.`,
  },
};

/**
 * A version of the legal page as a plain-text document, word for word, to send to a customer: what they
 * accepted, in the language they contracted in.
 */
export function legalDocument(version: string, lang: Lang) {
  const v = legalVersion(version);
  if (!v) throw new Error(`No legal version ${version}`);
  const t = v.text[lang],
    d = DOCUMENT[lang];
  const out = [
    `Foco · ${t.title}`,
    d.updated(legalDate(v.version, lang)),
    d.valid(
      legalDate(v.version, lang),
      v.until ? legalDate(v.until, lang) : null,
    ),
    `${LEGAL_URL}?v=${v.version}`,
  ];
  for (const id of LEGAL_SECTIONS) {
    const section = t.sections[id];
    out.push("", "", section.title.toUpperCase());
    for (const part of section.parts) {
      if (part.h) out.push("", part.h);
      if (part.p) out.push("", part.p);
      if (part.list) out.push("", ...part.list.map((item) => `- ${item}`));
    }
  }
  return { name: d.file(v.version), text: out.join("\n") + "\n" };
}
